import { useState, type ReactNode } from 'react';
import type { AdminRole } from '@/lib/admin/roles';
import AstraeaSidebar from './AstraeaSidebar';

export interface AstraeaAppShellProps {
  children: ReactNode;
  adminRole: AdminRole;
  balance: number | null;
  email: string;
  onChangePassword: () => void;
  onSignOut: () => void;
}

function AccountFooter({
  balance,
  email,
  onChangePassword,
  onSignOut,
}: Pick<AstraeaAppShellProps, 'balance' | 'email' | 'onChangePassword' | 'onSignOut'>) {
  return (
    <div className="px-1 py-4 border-t border-secondary-100 space-y-2">
      <div className="px-3 py-2 rounded-lg bg-background-100 border border-secondary-100">
        <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-700">
          <i className="ri-copper-coin-line" />
          {balance === null ? '…' : balance} 点
        </div>
        <div className="mt-1 text-[11px] text-secondary-400 truncate" title={email}>
          {email}
        </div>
      </div>
      <div className="flex gap-1">
        <button
          type="button"
          onClick={onChangePassword}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-[11px] font-medium text-foreground-600 hover:bg-secondary-100 cursor-pointer"
          title="修改密码"
        >
          <i className="ri-lock-password-line" />
          改密码
        </button>
        <button
          type="button"
          onClick={onSignOut}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-[11px] font-medium text-foreground-600 hover:bg-secondary-100 cursor-pointer"
          title="登出"
        >
          <i className="ri-logout-box-r-line" />
          登出
        </button>
      </div>
    </div>
  );
}

export default function AstraeaAppShell({
  children,
  adminRole,
  balance,
  email,
  onChangePassword,
  onSignOut,
}: AstraeaAppShellProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);
  const footerProps = { balance, email, onChangePassword, onSignOut };

  return (
    <div className="astraea-app-shell min-h-screen flex bg-background-100">
      <aside className="astraea-sidebar hidden lg:flex flex-col w-[var(--astraea-sidebar-width)] shrink-0 fixed inset-y-0 left-0 border-r border-secondary-200 bg-background-50 px-3 py-5 z-30">
        <AstraeaSidebar adminRole={adminRole} />
        <AccountFooter {...footerProps} />
      </aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-foreground-950/30"
            onClick={closeMobile}
            aria-hidden
          />
          <aside className="astraea-sidebar absolute inset-y-0 left-0 w-[min(18rem,88vw)] flex flex-col bg-background-50 px-3 py-5 shadow-2xl z-50">
            <AstraeaSidebar adminRole={adminRole} onNavigate={closeMobile} />
            <AccountFooter {...footerProps} />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-[var(--astraea-sidebar-width)] flex flex-col min-h-screen min-w-0">
        <header className="lg:hidden sticky top-0 z-20 flex items-center justify-between gap-3 px-4 py-3 bg-background-50/95 border-b border-secondary-200">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="w-9 h-9 rounded-xl border border-secondary-200 flex items-center justify-center text-foreground-700 cursor-pointer shrink-0"
              aria-label="打开导航"
            >
              <i className="ri-menu-line text-lg" />
            </button>
            <span className="font-semibold text-foreground-950 truncate">Astraea</span>
          </div>
          <span className="text-xs font-medium text-primary-700 shrink-0">
            <i className="ri-copper-coin-line mr-0.5" />
            {balance === null ? '…' : balance} 点
          </span>
        </header>
        <main className="astraea-main flex-1 min-w-0 min-h-0">{children}</main>
      </div>
    </div>
  );
}
